import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { ErrorAlert } from '../..';
import { FILTER_NAME_SIZE_LIMIT } from '../../../../../shared/savedFilters';
import titleCase from '../../../../../shared/utils/titleCase';
import { useInputValue } from '../../../../hooks';
import { onEnter } from '../../../../utils';
import Button, { ButtonGroup } from '../../Button';
import { LabeledTextInput } from '../../Forms';
import type { AsyncStatus } from '../../../../types';
import type { InputElement } from '../../Forms/InputBase';
import type { ResourceType } from '../types';

type Props = {
  resourceType: ResourceType;
  isDisplayed: boolean;
  isSaving: boolean;
  errors?: AsyncStatus['errors'];
  onSave: (name: string) => void;
  onHide: () => void;
};

/**
 * SaveAsModal allows the user to save the current filter state as a new {resourceType}.
 */
const SaveAsModal: VoidFunctionComponent<Props> = ({ resourceType, isDisplayed, isSaving, errors, onHide, onSave }) => {
  const [name, setName] = useInputValue();
  const isSubmitDisabled = isSaving || name.trim().length < 1;
  //  Submit on keydown
  const onEnterKeyDown = onEnter<InputElement>(() => !isSubmitDisabled && onSave(name));
  return (
    <Modal animation show={isDisplayed} onHide={onHide} onEscapeKeyDown={onHide}>
      <Modal.Header>
        <Modal.Title>Save {resourceType}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="margin-bottom-md">Your currently applied selections will be saved as a new {resourceType}.</div>
        <LabeledTextInput
          labelVariant="legacy"
          label="Name"
          placeholder={`Name ${resourceType}`}
          value={name}
          onChange={setName}
          onKeyDown={onEnterKeyDown}
          maxLength={FILTER_NAME_SIZE_LIMIT}
          showCharCount
          autoFocus
          testId={`saved-${resourceType}-modal-input--save-as`}
        />
        {errors && errors.length > 0 && (
          <div className="margin-top-md">
            <ErrorAlert status={{ errors }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={() => onSave(name)}
            isDisabled={isSubmitDisabled}
            testId={`saved-${resourceType}-modal-submit--save-as`}
          >
            Save {titleCase(resourceType)}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveAsModal;
